import type { PresetListing } from "@pax/api-contract/presets/v1alpha1";
import { focusHaloFlush, panelMotion, press, Skeleton } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { largestSource, srcSet } from "@/lib/images";
import { compactCount } from "../facts";
import { type CatalogSort, catalogSortName, sortCatalog } from "../ordering";
import { Rail } from "./Rail";

/**
 * The card's width, as a property so the artwork's own midpoint — where the
 * rail's step controls sit — can be derived from it rather than repeated.
 */
const CARD = "[--card:11rem] sm:[--card:13rem] lg:[--card:15rem]";
const ARTWORK_MIDDLE = "calc(var(--card) * 10 / 16 / 2)";

/** Named rather than counted, so a placeholder is not keyed by its position. */
const PLACEHOLDERS = ["a", "b", "c", "d", "e", "f"];

/**
 * One shelf of the store: the catalog in one order, as far along it as a row
 * can reach.
 *
 * Its heading is the way to the rest of that order. The search wall reads
 * the same three orders the shelves do, so pressing **Most played** opens the
 * whole catalog already sorted the way the row in front of you was.
 */
export function StoreRail({
  sort,
  listings,
  ranked = false,
}: {
  sort: CatalogSort;
  listings: PresetListing[];
  ranked?: boolean;
}) {
  return (
    <section className={CARD}>
      <Link
        to="/search"
        search={{ sort }}
        className={`mb-3 flex w-fit items-center gap-0.5 rounded-full text-base font-medium sm:text-lg ${panelMotion} ${focusHaloFlush}`}
      >
        {catalogSortName(sort)}
        <ChevronRight className="size-4 text-muted-foreground" />
      </Link>
      <Rail name={`shelf-${sort}`} arm={ARTWORK_MIDDLE}>
        {sortCatalog(listings, sort).map((listing, position) => (
          <Link
            key={listing.slug}
            to="/presets/$slug"
            params={{ slug: listing.slug }}
            className={`w-(--card) shrink-0 rounded-xl text-left hover:opacity-80 ${panelMotion} ${press} ${focusHaloFlush}`}
          >
            {/* Landscape, which is the shape covers are actually made in. A
                portrait crop takes a painting of the sky and returns a column
                of cloud. */}
            <img
              src={listing.cover ? largestSource(listing.cover) : undefined}
              srcSet={listing.cover ? srcSet(listing.cover) : undefined}
              alt=""
              loading="lazy"
              decoding="async"
              className="aspect-[16/10] w-full rounded-xl bg-foreground/5 object-cover ring-1 ring-foreground/10"
            />
            <span className="mt-2 flex items-baseline gap-2">
              {ranked && (
                <span className="text-lg leading-none font-semibold text-muted-foreground/40">
                  {position + 1}
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{listing.title}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {listing.regionCount.toLocaleString()} regions &middot;{" "}
                  {compactCount(listing.gamesStarted)} games
                </span>
              </span>
            </span>
          </Link>
        ))}
      </Rail>
    </section>
  );
}

export function StoreRailSkeleton({ ranked = false }: { ranked?: boolean }) {
  return (
    <section aria-busy className={CARD}>
      <Skeleton className="mb-3 h-7 w-36" />
      <div className="flex gap-3 overflow-hidden">
        {PLACEHOLDERS.map(placeholder => (
          <div key={placeholder} className="w-(--card) shrink-0">
            <Skeleton className="aspect-[16/10] w-full rounded-xl" />
            <Skeleton className={`mt-2 h-5 ${ranked ? "w-32" : "w-28"}`} />
            <Skeleton className="mt-1.5 h-4 w-24" />
          </div>
        ))}
      </div>
    </section>
  );
}
